import { LGraphNode, LiteGraph } from "litegraph.js";
import { addDrivableSlider, finalizeDrivableSliders, syncDrivableSliders } from "../utils/addDrivableSlider.js";

/**
 * ParticleEmitterNode
 * -------------------
 * Spawns particles with velocity, gravity, and lifetime.
 * Outputs a canvas of the particle field.
 */
export class ParticleEmitterNode extends LGraphNode {
  constructor() {
    super();
    this.title = "Particles";

    this.properties = {
      rate: 5,
      lifetime: 60,
      gravity: 0.5,
      speed: 3,
      size: 4,
      spread: 360,
      color: "#ffffff",
    };

    this._particles = [];

    this.addInput("Color", "string");
    this.addOutput("Canvas", "canvas");

    addDrivableSlider(this, "Rate", "rate", 5, 0, 30, 1);
    addDrivableSlider(this, "Lifetime", "lifetime", 60, 10, 300, 1);
    addDrivableSlider(this, "Gravity", "gravity", 0.5, -5, 5, 0.1);
    addDrivableSlider(this, "Speed", "speed", 3, 0, 20, 0.5);
    addDrivableSlider(this, "Size", "size", 4, 1, 30, 1);
    addDrivableSlider(this, "Spread", "spread", 360, 0, 360, 1);

    finalizeDrivableSliders(this, 1);

    this._outCanvas = document.createElement("canvas");
    this._outCanvas.width = 1500;
    this._outCanvas.height = 1500;
    this._outCtx = this._outCanvas.getContext("2d");

    this.size = [200, 180];
    this.mode = LiteGraph.ALWAYS;
    this.color = "#2a2a2a";
    this.bgcolor = "#333333";
  }

  onExecute() {
    syncDrivableSliders(this);

    const inputColor = this.getInputData(0);
    if (inputColor) this.properties.color = inputColor;

    const { rate, lifetime, gravity, speed, size, spread, color } = this.properties;
    const w = this._outCanvas.width;
    const h = this._outCanvas.height;
    const cx = w / 2;
    const cy = h / 2;

    // Emit new particles
    for (let i = 0; i < rate; i++) {
      const angleRange = (spread / 360) * Math.PI * 2;
      const angle = -Math.PI / 2 + (Math.random() - 0.5) * angleRange;
      this._particles.push({
        x: cx,
        y: cy,
        vx: Math.cos(angle) * speed * (0.5 + Math.random() * 0.5),
        vy: Math.sin(angle) * speed * (0.5 + Math.random() * 0.5),
        life: lifetime,
        maxLife: lifetime,
        size: size * (0.5 + Math.random()),
      });
    }

    // Update particles
    for (let i = this._particles.length - 1; i >= 0; i--) {
      const p = this._particles[i];
      p.vy += gravity * 0.1;
      p.x += p.vx;
      p.y += p.vy;
      p.life--;
      if (p.life <= 0) {
        this._particles.splice(i, 1);
      }
    }

    // Cap particle count
    if (this._particles.length > 2000) {
      this._particles.splice(0, this._particles.length - 2000);
    }

    // Draw
    const ctx = this._outCtx;
    ctx.clearRect(0, 0, w, h);

    for (const p of this._particles) {
      const alpha = p.life / p.maxLife;
      ctx.globalAlpha = alpha;
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size * alpha, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;

    this.setOutputData(0, this._outCanvas);
  }
}

LiteGraph.registerNodeType("generators/Particles", ParticleEmitterNode);
